import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import tw from 'tailwind.macro';
import { v4 as uuid } from 'uuid';
import { fadeIn, fadeOut } from '../animations/keyframes';
import { slideY } from '../animations/modal';
import Notification, { NotificationType } from '../components/Notification';
import { CheckIcon } from '../images/icons/check';
import { WarningIcon } from '../images/icons/warning';
import { colors } from '../styles/constants';
import { useDelayedAction } from './useDelayedAction';

export { NotificationType as ToastType };

const TOAST_TYPE_ICONS: { [key in NotificationType]: React.ReactNode } = {
  error: <WarningIcon color={colors['toast-error']} />,
  warning: <WarningIcon color={colors['toast-warning']} />,
  success: <CheckIcon color={colors['toast-success']} />,
};

interface ToastContainerProps {
  isRemoving: boolean;
  customStyle?: FlattenSimpleInterpolation;
}

const ToastContainer = styled.div<ToastContainerProps>`
  animation: ${slideY(10, 0)} 350ms cubic-bezier(0, 0, 0.2, 1),
    ${fadeIn} 350ms cubic-bezier(0, 0, 0.2, 1);

  ${({ isRemoving }) =>
    isRemoving &&
    css`
      animation: ${slideY(0, 10)} 350ms cubic-bezier(0, 0, 0.2, 1),
        ${fadeOut} 350ms cubic-bezier(0, 0, 0.2, 1);
    `}
  ${({ customStyle }) => customStyle}
`;

interface ToastProps {
  duration: number;
  type: NotificationType;
  onDelete: () => void;
  icon?: ReactNode;
  customStyle?: FlattenSimpleInterpolation;
}

export const Toast: React.FC<ToastProps> = ({
  children,
  customStyle,
  duration,
  icon,
  onDelete,
  type,
}) => {
  const { delay, isPending } = useDelayedAction();

  useEffect(() => {
    let timeoutId;
    if (duration !== Infinity) {
      timeoutId = setTimeout(() => delay(onDelete, 348), duration);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);
  return (
    <ToastContainer customStyle={customStyle} isRemoving={isPending}>
      <Notification
        children={children}
        icon={icon ?? TOAST_TYPE_ICONS[type]}
        type={type}
      />
    </ToastContainer>
  );
};

const ToastsContainer = styled.div<{
  customStyle?: FlattenSimpleInterpolation;
}>`
  ${tw`absolute flex flex-col`}
  bottom: 0;
  left: 0;
  right: 0;
  padding-left: inherit;
  padding-right: inherit;
  gap: var(--size-small);

  ${({ customStyle }) => customStyle}
`;

interface IToast {
  id: string;
  type: NotificationType;
  message: React.ReactNode;
  duration: number;
  icon?: ReactNode;
}

export type TToast = Omit<IToast, 'id'>;

export type UseToastsContext = {
  add: (
    toast: TToast,
    options?: {
      removeOthers?: boolean;
    },
  ) => string;
  remove: (id: string) => void;
  ToastsComponent: JSX.Element;
};

export const useToasts = (
  containerStyle?: FlattenSimpleInterpolation,
): UseToastsContext => {
  const [queue, setQueue] = useState<IToast[]>([]);

  const add = useCallback(
    (toast: TToast, options: { removeOthers?: boolean } = {}): string => {
      const { removeOthers = false } = options;
      const id = uuid();
      const formattedToast: IToast = { ...toast, id: id as string };

      setQueue((queue) =>
        removeOthers ? [formattedToast] : [...queue, formattedToast],
      );

      return id;
    },
    [],
  );

  const remove = useCallback((id: string) => {
    setQueue((queue) => queue.filter((toast) => toast.id !== id));
  }, []);

  const ToastsComponent = useMemo(
    () => (
      <ToastsContainer customStyle={containerStyle}>
        {queue.map((toast) => (
          <Toast
            key={toast.id}
            duration={toast.duration}
            icon={toast.icon}
            onDelete={() => remove(toast.id)}
            type={toast.type}
          >
            {toast.message}
          </Toast>
        ))}
      </ToastsContainer>
    ),
    [containerStyle, queue, remove],
  );

  return { add, remove, ToastsComponent };
};
